import { IconButton } from '@affine/component';
import type { ChatPanel } from '@affine/core/blocksuite/ai/chat-panel';
import { CHAT_FORM_MOTION_MS } from '@affine/core/blocksuite/ai/chat-panel/const';
import { GlobalStateService } from '@affine/core/modules/storage';
import {
  aiIslandAnchor$,
  type AIIslandAnchorScope,
} from '@affine/core/modules/streamlain/shared/ai-island-anchor';
import {
  STREAMLAIN_AI_ISLAND_INSET,
  type StreamlainAIIslandSnapPoint,
} from '@affine/core/modules/streamlain/shared/workspace-pane-layout';
import { ViewService } from '@affine/core/modules/workbench/services/view';
import { WorkbenchService } from '@affine/core/modules/workbench/services/workbench';
import { ViewCopilotOverlay } from '@affine/core/modules/workbench/view/view-islands';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { CloseIcon, MinusIcon } from '@blocksuite/icons/rc';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';
import { assignInlineVars, setElementVars } from '@vanilla-extract/dynamic';
import {
  type PointerEvent,
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { usePhoneSizedView } from '../../../components/ai-island/phone-sized-view';
import { aiIslandSnapPoint$ } from '../../../components/ai-island/snap';
import {
  type AIPanelEdge,
  aiPanelResizeEdges,
  type AIPanelSize,
  aiPanelSize$,
  resizeAIPanel,
  storeAIPanelSize,
} from './ai-panel-size';
import {
  blurChatPanelFocusWithin,
  type ChatPanelPresence,
  chatPanelPresence$,
  registerChatPanelBaseHost,
} from './chat-panel-host-registry';
import * as styles from './workspace-chat-sidebar-tab.css';
import { showWorkspaceCopilot } from './workspace-copilot-overlay-control';

const AI_PANEL_INLINE_END_PROPERTY = '--streamlain-ai-panel-inline-end';

const PANEL_GROW: Keyframe[] = [
  { opacity: 0, transform: 'scale(0.12)' },
  { opacity: 1, transform: 'scale(1)' },
];
const PANEL_GROW_TIMING: KeyframeAnimationOptions = {
  duration: 220,
  easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
};

/**
 * Stops a running grow or collapse. Cancelling rejects `finished` with an AbortError; the stop is
 * intended, so that rejection is handled here.
 */
function cancelPanelAnimation(animation: Animation) {
  animation.finished.catch(() => undefined);
  animation.cancel();
}

/**
 * The panel grows from the AI island and collapses back into it (transform-origin: the island's centre).
 * Returns whether it is displayed: it stays so while it collapses.
 */
function usePanelMorph(
  surface: RefObject<HTMLElement | null>,
  visible: boolean
) {
  const [displayed, setDisplayed] = useState(visible);
  const initial = useRef(true);
  useLayoutEffect(() => {
    const element = surface.current;
    const first = initial.current;
    initial.current = false;
    const still =
      first ||
      !element ||
      typeof element.animate !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (visible) {
      setDisplayed(true);
      if (still) return;
      const grow = element.animate(PANEL_GROW, PANEL_GROW_TIMING);
      return () => cancelPanelAnimation(grow);
    }
    if (still) {
      setDisplayed(false);
      return;
    }
    // Hold the collapsed frame until the panel is hidden: without the fill the finished
    // animation hands back the full-size panel for one frame before it unmounts.
    const collapse = element.animate(PANEL_GROW, {
      ...PANEL_GROW_TIMING,
      direction: 'reverse',
      fill: 'forwards',
    });
    collapse.onfinish = () => setDisplayed(false);
    return () => cancelPanelAnimation(collapse);
  }, [surface, visible]);
  return displayed;
}

const FORM_TIMING: KeyframeAnimationOptions = {
  duration: CHAT_FORM_MOTION_MS,
  easing: 'ease-out',
};

/**
 * The chat's form. Minimized: its controls and its input, the history put away.
 * Lean: a chat block is chosen on the canvas; the chat is minimized and stays
 * so, because the answers land in the block.
 */
type PanelForm = 'full' | 'lean' | 'minimized';
const formOf = (panel: ChatPanel): PanelForm =>
  panel.blockTurns !== null ? 'lean' : panel.minimized ? 'minimized' : 'full';

/** The chat's form; the window follows its height in one movement. */
function usePanelForm(
  surface: RefObject<HTMLElement | null>,
  panel: ChatPanel | null,
  visible: boolean
) {
  const [form, setForm] = useState<PanelForm>('full');
  const shown = useRef<PanelForm>('full');
  /** The window's height when the form changed, before it follows. */
  const from = useRef<number | null>(null);
  useEffect(() => {
    if (!panel) return;
    // Fired before the chat renders its new form: the window still has its old height here. The chat
    // renders before React does, so the window keeps that height right away, not only from the layout
    // effect on, or its new content would lay out in the old window's auto height for a frame.
    const sync = () => {
      const next = formOf(panel);
      const element = surface.current;
      from.current = element?.offsetHeight ?? null;
      if (element && from.current !== null && next !== shown.current)
        element.style.height = `${from.current}px`;
      shown.current = next;
      setForm(next);
    };
    shown.current = formOf(panel);
    setForm(shown.current);
    panel.addEventListener('lean-change', sync);
    return () => panel.removeEventListener('lean-change', sync);
  }, [panel, surface]);
  useLayoutEffect(() => {
    const element = surface.current;
    const start = from.current;
    from.current = null;
    if (!element) return;
    if (
      start === null ||
      !visible ||
      typeof element.animate !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      element.style.height = '';
      return;
    }
    let resize: Animation | undefined;
    // The chat's content takes its new form in its own update; hold the window until it has.
    element.style.height = `${start}px`;
    const frame = requestAnimationFrame(() => {
      element.style.height = '';
      const end = element.offsetHeight;
      if (end !== start)
        resize = element.animate(
          [{ height: `${start}px` }, { height: `${end}px` }],
          FORM_TIMING
        );
    });
    return () => {
      cancelAnimationFrame(frame);
      element.style.height = '';
      if (resize) cancelPanelAnimation(resize);
    };
    // Only a change of form moves the window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  return form;
}

/** The history's parts that fade in when it comes back: its messages, its controls and the host's buttons. */
const HISTORY_PARTS =
  '.chat-panel-messages-layer, ai-chat-panel-title, [data-history-control]';
const FADE_IN: Keyframe[] = [{ opacity: 0 }, { opacity: 1 }];

/** Presses here never put the chat history away: island, dock and floating layers. */
const RETRACT_IGNORE =
  '[data-copilot-ignore],.blocksuite-portal,[data-radix-popper-content-wrapper],[role="dialog"]';

/**
 * A press anywhere in the window beside the open panel (view, sidebar, header) puts the history
 * away: its surface folds down into one line of the chat over the input (chat-history-peek), in the
 * same movement as the window's height. That line, an answer the user asked for or a question of
 * the assistant brings it back: the surface grows out of the line and the history fades in. Focus on
 * the input does not, so a short question leaves the view free. Presses on RETRACT_IGNORE do not count.
 */
function useHistoryRetract(
  element: HTMLElement | null,
  panel: ChatPanel | null,
  visible: boolean
) {
  useEffect(() => {
    if (!element || !panel || !visible) return;
    const page = element.ownerDocument;
    const still = () =>
      typeof element.animate !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let motion: Animation[] = [];
    const settle = () => {
      motion.forEach(cancelPanelAnimation);
      motion = [];
    };
    let wasMinimized = panel.minimized;
    // Fired before the chat renders its new form, so the history still has its height here.
    const onFormChange = () => {
      const away = !wasMinimized && panel.minimized;
      const back = wasMinimized && !panel.minimized;
      wasMinimized = panel.minimized;
      settle();
      if ((!away && !back) || still()) return;
      const history = element.querySelector<HTMLElement>(
        '.chat-panel-messages-layer'
      );
      const from = away ? (history?.offsetHeight ?? 0) : 0;
      requestAnimationFrame(() => {
        if (away) {
          const peek = element.querySelector<HTMLElement>('chat-history-peek');
          if (!peek || !from) return;
          motion = [
            peek.animate(
              [{ height: `${from}px` }, { height: `${peek.offsetHeight}px` }],
              FORM_TIMING
            ),
            peek.animate(FADE_IN, FORM_TIMING),
          ];
          return;
        }
        motion = Array.from(
          element.querySelectorAll<HTMLElement>(HISTORY_PARTS)
        ).map(part => part.animate(FADE_IN, FORM_TIMING));
      });
    };
    const onPointerDown = (event: globalThis.PointerEvent) => {
      const target = event.target;
      if (
        !(target instanceof Element) ||
        element.contains(target) ||
        target.closest(RETRACT_IGNORE) ||
        panel.minimized ||
        panel.blockTurns !== null
      )
        return;
      panel.minimized = true;
    };
    page.addEventListener('pointerdown', onPointerDown, true);
    panel.addEventListener('lean-change', onFormChange);
    return () => {
      page.removeEventListener('pointerdown', onPointerDown, true);
      panel.removeEventListener('lean-change', onFormChange);
      settle();
    };
  }, [element, panel, visible]);
}

/**
 * The panel docks to the AI island: it lies so that the island, which keeps its place, sits in its
 * input's action row. It follows the island's anchor and its own frame, both in the window's
 * coordinates, so the two move as one when the view or the window resizes. It docks while the
 * panel is laid out: a panel that is not displayed has no frame to lie in.
 */
function useDockedToAIIsland(
  surface: HTMLElement | null,
  scope: AIIslandAnchorScope,
  layoutable: boolean
) {
  useLayoutEffect(() => {
    if (!layoutable) return;
    const frame = surface?.offsetParent;
    if (!surface || !(frame instanceof HTMLElement)) return;
    let anchor: { x: number; y: number } | null = null;
    const place = () => {
      if (!anchor) return;
      const box = frame.getBoundingClientRect();
      setElementVars(surface, {
        [styles.islandX]: `${anchor.x - box.left}px`,
        [styles.islandY]: `${box.bottom - anchor.y}px`,
      });
    };
    const subscription = aiIslandAnchor$(scope).subscribe(next => {
      anchor = next;
      place();
    });
    const observer = new ResizeObserver(place);
    observer.observe(frame);
    return () => {
      subscription.unsubscribe();
      observer.disconnect();
    };
  }, [layoutable, scope, surface]);
}

/**
 * Resizes the panel by its edges and top corners. While the user drags, the live size follows the
 * pointer; on release it is stored as rendered (the view clamps it), a preview's width left out.
 */
function usePanelResize(
  surface: RefObject<HTMLElement | null>,
  snap: StreamlainAIIslandSnapPoint,
  phone: boolean
) {
  const globalState = useService(GlobalStateService).globalState;
  const stored = useLiveData(
    useMemo(() => aiPanelSize$(globalState, phone), [globalState, phone])
  );
  const [live, setLive] = useState<AIPanelSize | null>(null);
  /** The live size for the release handler, which must not wait for a render. */
  const liveRef = useRef<AIPanelSize | null>(null);
  const showLive = useCallback((size: AIPanelSize | null) => {
    liveRef.current = size;
    setLive(size);
  }, []);
  const press = useRef<{
    pointerId: number;
    edge: AIPanelEdge;
    x: number;
    y: number;
    start: AIPanelSize;
  } | null>(null);

  const onPointerDown = useCallback(
    (edge: AIPanelEdge, event: PointerEvent<HTMLElement>) => {
      const element = surface.current;
      if (!element || event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      press.current = {
        pointerId: event.pointerId,
        edge,
        x: event.clientX,
        y: event.clientY,
        start: { width: element.offsetWidth, height: element.offsetHeight },
      };
    },
    [surface]
  );
  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const current = press.current;
      if (current?.pointerId !== event.pointerId) return;
      showLive(
        resizeAIPanel(current.start, current.edge, snap, {
          dx: event.clientX - current.x,
          dy: event.clientY - current.y,
        })
      );
    },
    [showLive, snap]
  );
  const onPointerEnd = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const element = surface.current;
      const resized = liveRef.current;
      if (press.current?.pointerId !== event.pointerId) return;
      press.current = null;
      if (element && resized && event.type === 'pointerup') {
        storeAIPanelSize(
          globalState,
          {
            width: Math.min(resized.width, element.offsetWidth),
            height: Math.min(resized.height, element.offsetHeight),
          },
          phone
        );
      }
      showLive(null);
    },
    [globalState, phone, showLive, surface]
  );

  return {
    size: live ?? stored,
    edges: aiPanelResizeEdges(snap, phone),
    onPointerDown,
    onPointerMove,
    onPointerEnd,
  };
}

/** The root's existing Workbench remains the only authority for the active view. */
export function ActiveViewChatHost({ children }: PropsWithChildren) {
  const workbench = useService(WorkbenchService).workbench;
  const view = useLiveData(workbench.activeView$);
  return <FrameworkScope scope={view.scope}>{children}</FrameworkScope>;
}

/** Presentation/hosting only: never reads workspace snapshots or document bodies. */
export function WorkspaceCopilotOverlaySurface({
  panel,
  preparePanel,
  previewWidth,
  children,
}: PropsWithChildren<{
  panel: ChatPanel | null;
  preparePanel?: () => void;
  /** The width a shown preview needs at least. */
  previewWidth?: number;
}>) {
  const t = useI18n();
  const workspaceId = useService(WorkspaceService).workspace.id;
  const view = useService(ViewService).view;
  const scope = useMemo(
    () => ({ workspaceId, viewId: view.id }),
    [workspaceId, view.id]
  );
  const [base, setBase] = useState<HTMLDivElement | null>(null);
  const [presence, setPresence] = useState<ChatPanelPresence>('hidden');
  const surface = useRef<HTMLElement>(null);
  // The panel mounts through the view's portal, after this component's first effects.
  const [mounted, setMounted] = useState<HTMLElement | null>(null);
  const attach = useCallback((element: HTMLElement | null) => {
    surface.current = element;
    setMounted(element);
  }, []);
  useEffect(() => {
    const subscription = chatPanelPresence$(scope).subscribe(setPresence);
    return () => subscription.unsubscribe();
  }, [scope]);
  useEffect(() => {
    if (!base?.isConnected) return;
    // The portal target may arrive after the owner's initial render. Initialize
    // the existing panel before the first actual connection, on this real base.
    preparePanel?.();
    return registerChatPanelBaseHost(scope, panel, base);
  }, [base, panel, preparePanel, scope]);
  const visible = presence === 'surface';
  const displayed = usePanelMorph(surface, visible);
  const form = usePanelForm(surface, panel, visible);
  const lean = form === 'lean';
  const minimized = form === 'minimized';
  // The surface mounts through the view's portal after the first effects: the hook takes the
  // mounted element, so it starts once the surface is there.
  useHistoryRetract(mounted, panel, visible);
  useDockedToAIIsland(mounted, scope, visible || displayed);
  const globalState = useService(GlobalStateService).globalState;
  // A phone-sized view keeps the island centred (ai-island): the panel spans the view.
  const phone = usePhoneSizedView(useLiveData(view.contentElement$));
  const storedSnap = useLiveData(
    useMemo(() => aiIslandSnapPoint$(globalState), [globalState])
  );
  const snap = phone ? 'bottom-center' : storedSnap;
  const resize = usePanelResize(surface, snap, phone);
  useLayoutEffect(() => {
    const element = surface.current;
    if (!element) return;
    if (!visible) blurChatPanelFocusWithin(element);
    return () => blurChatPanelFocusWithin(element);
  }, [scope, visible]);
  // Above a bottom-right island the open panel takes the view's right end: its rendered width plus
  // its inset, for the view's floating controls (zoom toolbar, canvas fit). Narrow views clamp the
  // panel below its preferred width, so the measured box is the source, not the width setting.
  useLayoutEffect(() => {
    const element = surface.current;
    const view = element?.closest<HTMLElement>('[data-workbench-view]');
    if (!element || !view || !visible || snap !== 'bottom-right') return;
    const publish = () =>
      view.style.setProperty(
        AI_PANEL_INLINE_END_PROPERTY,
        `${element.offsetWidth + STREAMLAIN_AI_ISLAND_INSET}px`
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(element);
    return () => {
      observer.disconnect();
      view.style.removeProperty(AI_PANEL_INLINE_END_PROPERTY);
    };
  }, [snap, visible]);
  return (
    <ViewCopilotOverlay>
      <section
        ref={attach}
        data-testid="workspace-copilot-overlay"
        data-workspace-copilot
        data-workspace-view-id={view.id}
        data-copilot-ignore
        role="complementary"
        aria-label={t['com.streamlain.rightSidebar.ai-chat']()}
        className={styles.overlay}
        data-snap={snap}
        data-phone={phone}
        data-lean={lean}
        data-minimized={minimized}
        hidden={!visible}
        onKeyDown={event => {
          if (event.key !== 'Escape' || event.defaultPrevented || !visible)
            return;
          if (showWorkspaceCopilot(scope, false).ok) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        style={{
          display: visible || displayed ? 'flex' : 'none',
          ...assignInlineVars({
            ...(resize.size && {
              [styles.panelWidth]: `${resize.size.width}px`,
              [styles.panelHeight]: `${resize.size.height}px`,
            }),
            ...(previewWidth !== undefined && {
              [styles.panelPreviewWidth]: `${previewWidth}px`,
            }),
          }),
        }}
      >
        {(form === 'full' ? resize.edges : []).map(edge => (
          <div
            key={edge}
            className={styles.resizeHandle}
            data-edge={edge}
            data-testid="ai-panel-resize-handle"
            onPointerDown={event => resize.onPointerDown(edge, event)}
            onPointerMove={resize.onPointerMove}
            onPointerUp={resize.onPointerEnd}
            onPointerCancel={resize.onPointerEnd}
          />
        ))}
        <div ref={setBase} className={styles.panelHost}>
          {children}
        </div>
        {/* Puts the history away; its line over the input brings it back. */}
        <IconButton
          className={styles.overlayMinimize}
          hidden={lean || minimized || !panel}
          size="16"
          iconClassName={styles.overlayIcon}
          data-testid="ai-panel-minimize"
          data-history-control
          aria-label={t['com.streamlain.ai.chat.history.hide']()}
          onClick={() => {
            if (panel) panel.minimized = true;
          }}
        >
          <MinusIcon />
        </IconButton>
        {/* One close for every panel state (gated, loading, chat), at the right end of the controls.
            Minimized, the island and Escape close the panel. */}
        <IconButton
          className={styles.overlayClose}
          hidden={minimized}
          data-testid="ai-panel-close"
          data-history-control
          size="16"
          iconClassName={styles.overlayIcon}
          aria-label={t['com.streamlain.streamlain.common.close']()}
          onClick={() => showWorkspaceCopilot(scope, false)}
        >
          <CloseIcon />
        </IconButton>
      </section>
    </ViewCopilotOverlay>
  );
}
